import React, { useRef, useState, useEffect } from 'react';
import { Link, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Users, Video, FolderOpen, Gift, BarChart3,
  ChevronLeft, Plus, Edit, Trash2, Check, X, Search, Settings, Image,
  CreditCard, Tag, Upload, Calendar, Megaphone, Award, Share2, Menu, FileText, Radio
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API, LanguageToggle } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import UserEditDialog from '../../components/admin/UserEditDialog';
import VideoUploadDialog from './VideoUploadDialog';
import AnalyticsPage from './AnalyticsPage';
import BadgesManagement from './BadgesManagement';
import CampaignsManagement from './CampaignsManagement';
import ReferralsManagement from './ReferralsManagement';
import SegmentsManagement from './SegmentsManagement';
import SeminarsManagement from './SeminarsManagement';
import LiveSeminarsManagement from './LiveSeminarsManagement';
import ImageGuide from './ImageGuide';
import ContentManagement from './ContentManagement';
import PlansManagement from './PlansManagement';

const adminSelectContentClass =
  "z-[9999] bg-[#111111] border border-white/15 text-white shadow-xl";
const adminSelectItemClass =
  "text-white focus:bg-primary/20 focus:text-white";
const adminDatePickerStyle = { colorScheme: 'dark' };

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sidebarLinks = [
    { href: '/admin', icon: LayoutDashboard, label: t('admin.dashboard') },
    { href: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { href: '/admin/users', icon: Users, label: t('admin.users') },
    { href: '/admin/categories', icon: FolderOpen, label: t('admin.categories') },
    { href: '/admin/videos', icon: Video, label: t('admin.videos') },
    { href: '/admin/seminars', icon: Calendar, label: 'Seminars' },
    { href: '/admin/live-seminars', icon: Radio, label: 'Live Seminars' },
    { href: '/admin/plans', icon: CreditCard, label: i18n.language === 'el' ? 'Πλάνα Συνδρομών' : 'Subscription Plans' },
    { href: '/admin/coupons', icon: Tag, label: i18n.language === 'el' ? 'Κουπόνια' : 'Coupons' },
    { href: '/admin/subscriptions', icon: BarChart3, label: i18n.language === 'el' ? 'Συνδρομές & Μετάβαση' : 'Subscriptions & Migration' },
    { href: '/admin/grants', icon: Gift, label: t('admin.grants') },
    { href: '/admin/campaigns', icon: Megaphone, label: 'Campaigns' },
    { href: '/admin/content', icon: FileText, label: 'Content' },
    { href: '/admin/segments', icon: Users, label: 'Segments' },
    { href: '/admin/badges', icon: Award, label: 'Badges' },
    { href: '/admin/referrals', icon: Share2, label: 'Referrals' },
    { href: '/admin/image-guide', icon: Image, label: 'Image Guide' },
    { href: '/admin/settings', icon: Settings, label: i18n.language === 'el' ? 'Ρυθμίσεις' : 'Settings' },
  ];

  const SidebarNav = ({ mobile = false }) => (
    <div className="p-4 sm:p-6 min-h-full flex flex-col">
      <Link
        to="/"
        onClick={() => mobile && setDrawerOpen(false)}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 min-w-0"
      >
        <ChevronLeft size={20} className="shrink-0" />
        <span className="font-roboto font-bold tracking-wider text-sm break-words">MISEL GROUP DIGITAL LAB</span>
      </Link>
      <nav className="space-y-2 flex-1 overflow-y-auto pr-1">
        {sidebarLinks.map((link) => {
          const Icon = link.icon;
          const isActive = location.pathname === link.href;
          return (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => mobile && setDrawerOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-sm transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon size={18} className="shrink-0" />
              <span className="truncate">{link.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="pt-4">
        <LanguageToggle />
      </div>
    </div>
  );

  return (
    <div className="admin-shell min-h-screen bg-misel-bg lg:flex overflow-x-hidden">
      <aside className="hidden lg:block w-64 bg-misel-card border-r border-white/10 fixed h-full overflow-y-auto">
        <SidebarNav />
      </aside>

      <div className="lg:hidden sticky top-0 z-[80] bg-misel-card/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-sm border border-white/15 text-white"
          aria-label="Open admin navigation"
        >
          <Menu size={22} />
        </button>
        <span className="text-white font-roboto font-bold text-sm tracking-wide truncate px-3">Admin</span>
        <LanguageToggle />
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[100]">
          <button
            type="button"
            aria-label="Close admin navigation"
            className="absolute inset-0 bg-black/70 w-full h-full"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="relative h-full w-[min(18rem,86vw)] bg-misel-card border-r border-white/10 shadow-2xl">
            <SidebarNav mobile />
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="admin-content lg:ml-64 flex-1 min-w-0 w-full p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <Routes>
          <Route path="/" element={<DashboardOverview />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/users" element={<UsersManagement />} />
          <Route path="/categories" element={<CategoriesManagement />} />
          <Route path="/videos" element={<VideosManagement />} />
          <Route path="/seminars" element={<SeminarsManagement />} />
          <Route path="/live-seminars" element={<LiveSeminarsManagement />} />
          <Route path="/plans" element={<PlansManagement />} />
          <Route path="/coupons" element={<CouponsManagement />} />
          <Route path="/subscriptions" element={<SubscriptionsManagement />} />
          <Route path="/grants" element={<GrantsManagement />} />
          <Route path="/campaigns" element={<CampaignsManagement />} />
          <Route path="/content" element={<ContentManagement />} />
          <Route path="/segments" element={<SegmentsManagement />} />
          <Route path="/badges" element={<BadgesManagement />} />
          <Route path="/referrals" element={<ReferralsManagement />} />
          <Route path="/image-guide" element={<ImageGuide />} />
          <Route path="/settings" element={<SiteSettings />} />
        </Routes>
      </main>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/admin/analytics`, { headers });
        setAnalytics(res.data);
      } catch (e) {
        console.error('Failed to fetch analytics', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [token]);

  const stats = analytics ? [
    { label: i18n.language === 'el' ? 'Χρήστες' : 'Users', value: analytics.total_users },
    { label: i18n.language === 'el' ? 'Βίντεο' : 'Videos', value: analytics.total_videos },
    { label: i18n.language === 'el' ? 'Ενεργές Συνδρομές' : 'Active Subscriptions', value: analytics.active_subscriptions },
    { label: i18n.language === 'el' ? 'PPV Αγορές' : 'PPV Purchases', value: analytics.total_ppv_purchases },
  ] : [];

  return (
    <div>
      <h1 className="font-roboto font-bold text-2xl text-white mb-8">
        {i18n.language === 'el' ? 'Πίνακας Ελέγχου' : 'Dashboard'}
      </h1>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-misel-card animate-pulse rounded-sm" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-misel-card border border-white/10 rounded-sm p-6">
              <p className="text-gray-400 text-sm mb-2">{stat.label}</p>
              <p className="text-3xl font-bold text-white">{stat.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Users Management
const UsersManagement = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userEditOpen, setUserEditOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data);
    } catch (e) {
      console.error('Failed to fetch users', e);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, role) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/users/${userId}/role?role=${role}`, {}, { headers });
      toast.success(i18n.language === 'el' ? 'Ο ρόλος ενημερώθηκε' : 'Role updated');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const openUserEdit = (userId) => {
    setSelectedUserId(userId);
    setUserEditOpen(true);
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(search.toLowerCase()) ||
    user.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-roboto font-bold text-2xl text-white">{t('admin.users')}</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('nav.search')}
            className="pl-10 bg-white/5 border-white/10 text-white w-64"
          />
        </div>
      </div>

      <div className="bg-misel-card border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">Email</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Όνομα' : 'Name'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Ρόλος' : 'Role'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Ενέργειες' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className="border-t border-white/5">
                <td className="p-4 text-white">{user.email}</td>
                <td className="p-4 text-gray-400">{user.name}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-sm text-xs ${
                    user.role === 'admin' ? 'bg-primary/20 text-primary' : 'bg-white/10 text-gray-400'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                  <Select
                    value={user.role}
                    onValueChange={(value) => updateRole(user.id, value)}
                  >
                    <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      <SelectItem className={adminSelectItemClass} value="user">User</SelectItem>
                      <SelectItem className={adminSelectItemClass} value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      onClick={() => openUserEdit(user.id)}
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserEditDialog
        open={userEditOpen}
        onClose={setUserEditOpen}
        userId={selectedUserId}
        token={token}
        onUpdate={fetchUsers}
      />
    </div>
  );
};

// Categories Management
const CategoriesManagement = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryImageUploading, setCategoryImageUploading] = useState(false);
  const categoryImageInputRef = useRef(null);
  
  const [form, setForm] = useState({
    slug: '',
    translations: {
      el: { title: '', description: '' },
      en: { title: '', description: '' }
    },
    image_url: '',
    image_alt_el: '',
    image_alt_en: ''
  });

  useEffect(() => {
    fetchCategories();
  }, [token]);

  const fetchCategories = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/categories?locale=${i18n.language}`, { headers });
      setCategories(res.data);
    } catch (e) {
      console.error('Failed to fetch categories', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, form, { headers });
        toast.success(i18n.language === 'el' ? 'Η κατηγορία ενημερώθηκε' : 'Category updated');
      } else {
        await axios.post(`${API}/admin/categories`, form, { headers });
        toast.success(i18n.language === 'el' ? 'Η κατηγορία δημιουργήθηκε' : 'Category created');
      }
      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(i18n.language === 'el' ? 'Αρχειοθέτηση κατηγορίας;' : 'Archive category?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/categories/${id}`, { headers });
      toast.success(i18n.language === 'el' ? 'Η κατηγορία αρχειοθετήθηκε' : 'Category archived');
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const resetForm = () => {
    setForm({
      slug: '',
      translations: { el: { title: '', description: '' }, en: { title: '', description: '' } },
      image_url: '',
      image_alt_el: '',
      image_alt_en: ''
    });
    setEditingCategory(null);
  };

  const openEditCategory = (category) => {
    const translations = category.translations || {};
    setEditingCategory(category);
    setForm({
      slug: category.slug || '',
      translations: {
        el: {
          title: translations.el?.title || category.title || '',
          description: translations.el?.description || category.description || ''
        },
        en: {
          title: translations.en?.title || '',
          description: translations.en?.description || ''
        }
      },
      image_url: category.image_url || '',
      image_alt_el: category.image_alt_el || '',
      image_alt_en: category.image_alt_en || ''
    });
    setDialogOpen(true);
  };

  const uploadCategoryImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCategoryImageUploading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/uploads/image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm((prev) => ({ ...prev, image_url: res.data?.url || prev.image_url }));
      toast.success(i18n.language === 'el' ? 'Η εικόνα ανέβηκε' : 'Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image upload failed');
    } finally {
      setCategoryImageUploading(false);
      if (categoryImageInputRef.current) categoryImageInputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-roboto font-bold text-2xl text-white">{t('admin.categories')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" onClick={resetForm}>
              <Plus size={18} className="mr-2" />
              {t('admin.add_new')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-misel-card border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-roboto font-bold">
                {editingCategory 
                  ? (i18n.language === 'el' ? 'Επεξεργασία Κατηγορίας' : 'Edit Category')
                  : (i18n.language === 'el' ? 'Νέα Κατηγορία' : 'New Category')
                }
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-gray-400">Slug</label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="cutting"
                />
              </div>
              
              <Tabs defaultValue="el" className="w-full">
                <TabsList className="bg-white/5">
                  <TabsTrigger value="el">Ελληνικά</TabsTrigger>
                  <TabsTrigger value="en">English</TabsTrigger>
                </TabsList>
                <TabsContent value="el" className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-gray-400">Τίτλος (EL)</label>
                    <Input
                      value={form.translations.el.title}
                      onChange={(e) => setForm({
                        ...form,
                        translations: { ...form.translations, el: { ...form.translations.el, title: e.target.value } }
                      })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Περιγραφή (EL)</label>
                    <Input
                      value={form.translations.el.description}
                      onChange={(e) => setForm({
                        ...form,
                        translations: { ...form.translations, el: { ...form.translations.el, description: e.target.value } }
                      })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </TabsContent>
                <TabsContent value="en" className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-gray-400">Title (EN)</label>
                    <Input
                      value={form.translations.en.title}
                      onChange={(e) => setForm({
                        ...form,
                        translations: { ...form.translations, en: { ...form.translations.en, title: e.target.value } }
                      })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Description (EN)</label>
                    <Input
                      value={form.translations.en.description}
                      onChange={(e) => setForm({
                        ...form,
                        translations: { ...form.translations, en: { ...form.translations.en, description: e.target.value } }
                      })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <div>
                <label className="text-sm text-gray-400">Image URL</label>
                {form.image_url && (
                  <img src={form.image_url} alt={form.image_alt_en || form.image_alt_el || form.translations.en.title || form.translations.el.title} className="mt-2 mb-3 w-full h-36 object-cover rounded-sm border border-white/10" />
                )}
                <div className="mt-1 flex flex-col sm:flex-row gap-2">
                  <Input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="/api/images/..."
                  />
                  <input ref={categoryImageInputRef} type="file" accept="image/*" onChange={uploadCategoryImage} className="hidden" />
                  <Button type="button" variant="outline" disabled={categoryImageUploading} onClick={() => categoryImageInputRef.current?.click()} className="border-white/20 text-white hover:bg-white/10 shrink-0">
                    <Upload size={16} className="mr-2" />
                    {categoryImageUploading ? 'Uploading...' : 'Upload image'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Image alt EL</label>
                  <Input
                    value={form.image_alt_el}
                    onChange={(e) => setForm({ ...form, image_alt_el: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400">Image alt EN</label>
                  <Input
                    value={form.image_alt_en}
                    onChange={(e) => setForm({ ...form, image_alt_en: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/20 text-white">
                  {t('admin.cancel')}
                </Button>
                <Button onClick={handleSubmit} className="bg-primary hover:bg-primary/90">
                  {t('admin.save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="bg-misel-card border border-white/10 rounded-sm overflow-hidden">
            <div 
              className="h-32 bg-cover bg-center"
              style={{ backgroundImage: `url('${category.image_url || 'https://images.pexels.com/photos/15659458/pexels-photo-15659458.jpeg'}')` }}
            />
            <div className="p-4">
              <h3 className="text-white font-medium mb-1">{category.title}</h3>
              <p className="text-gray-500 text-sm mb-4 line-clamp-2">{category.description}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  title={i18n.language === 'el' ? 'Επεξεργασία κατηγορίας' : 'Edit category'}
                  onClick={() => openEditCategory(category)}
                >
                  <Edit size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  title={i18n.language === 'el' ? 'Αρχειοθέτηση κατηγορίας' : 'Archive category'}
                  onClick={() => handleDelete(category.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Videos Management
const VideosManagement = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoEditorOpen, setVideoEditorOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoEditorTab, setVideoEditorTab] = useState('details');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [vidRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/videos?locale=${i18n.language}`, { headers }),
        axios.get(`${API}/admin/categories?locale=${i18n.language}`, { headers })
      ]);
      setVideos(vidRes.data);
      setCategories(catRes.data);
    } catch (e) {
      console.error('Failed to fetch data', e);
    } finally {
      setLoading(false);
    }
  };

  const openNewVideo = () => {
    setEditingVideo(null);
    setSelectedVideoId(null);
    setVideoEditorTab('details');
    setVideoEditorOpen(true);
  };

  const openEditVideo = (video) => {
    setEditingVideo(video);
    setSelectedVideoId(video.id);
    setVideoEditorTab('details');
    setVideoEditorOpen(true);
  };

  const openVideoUpload = (video) => {
    setEditingVideo(video);
    setSelectedVideoId(video.id);
    setVideoEditorTab('upload');
    setVideoEditorOpen(true);
  };

  const toggleFeatured = async (videoId, isFeatured) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/videos/${videoId}/featured?is_featured=${!isFeatured}`, {}, { headers });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-roboto font-bold text-2xl text-white">{t('admin.videos')}</h1>
        <Button className="bg-primary hover:bg-primary/90" onClick={openNewVideo}>
          <Plus size={18} className="mr-2" />
          {t('admin.add_new')}
        </Button>
      </div>

      <div className="bg-misel-card border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">Slug</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Κατηγορία' : 'Category'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Κατάσταση' : 'Status'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Featured' : 'Featured'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Ενέργειες' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video) => (
              <tr key={video.id} className="border-t border-white/5">
                <td className="p-4 text-white">{video.slug}</td>
                <td className="p-4 text-gray-400">{video.category_title || video.category_id}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-sm text-xs ${
                    video.is_published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {video.is_published ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td className="p-4">
                  <button
                    onClick={() => toggleFeatured(video.id, video.is_featured)}
                    className={`p-1 rounded ${video.is_featured ? 'text-primary' : 'text-gray-600'}`}
                  >
                    {video.is_featured ? <Check size={18} /> : <X size={18} />}
                  </button>
                </td>
                <td className="p-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className="mr-2 border-white/20 text-white hover:bg-white/10"
                    onClick={() => openEditVideo(video)}
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mr-2 border-primary/50 text-primary hover:bg-primary/10"
                    onClick={() => openVideoUpload(video)}
                  >
                    <Upload size={14} className="mr-1" />
                    Upload/Replace
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <VideoUploadDialog
        open={videoEditorOpen}
        onOpenChange={setVideoEditorOpen}
        video={editingVideo}
        videoId={selectedVideoId}
        categories={categories}
        token={token}
        initialTab={videoEditorTab}
        onUploadComplete={fetchData}
      />
    </div>
  );
};

// Grants Management
const GrantsManagement = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [grants, setGrants] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [form, setForm] = useState({
    user_id: '',
    access_type: 'global',
    target_id: '',
    expires_at: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [grantsRes, usersRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/grants`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/categories?locale=${i18n.language}`)
      ]);
      setGrants(grantsRes.data);
      setUsers(usersRes.data);
      setCategories(catRes.data);
    } catch (e) {
      console.error('Failed to fetch data', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/grants`, form, { headers });
      toast.success(i18n.language === 'el' ? 'Η παραχώρηση δημιουργήθηκε' : 'Grant created');
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleRevoke = async (id) => {
    if (!confirm(i18n.language === 'el' ? 'Είστε σίγουροι;' : 'Are you sure?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/grants/${id}`, { headers });
      toast.success(i18n.language === 'el' ? 'Η παραχώρηση ανακλήθηκε' : 'Grant revoked');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-roboto font-bold text-2xl text-white">{t('admin.grants')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus size={18} className="mr-2" />
              {t('admin.add_new')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-misel-card border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-roboto font-bold">
                {i18n.language === 'el' ? 'Νέα Παραχώρηση' : 'New Grant'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Χρήστης' : 'User'}</label>
                <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select user..." />
                  </SelectTrigger>
                  <SelectContent className={adminSelectContentClass}>
                    {users.map((user) => (
                      <SelectItem className={adminSelectItemClass} key={user.id} value={user.id}>{user.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Τύπος Πρόσβασης' : 'Access Type'}</label>
                <Select value={form.access_type} onValueChange={(v) => setForm({ ...form, access_type: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={adminSelectContentClass}>
                    <SelectItem className={adminSelectItemClass} value="global">Global (All Content)</SelectItem>
                    <SelectItem className={adminSelectItemClass} value="category">Category</SelectItem>
                    <SelectItem className={adminSelectItemClass} value="video">Single Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.access_type === 'category' && (
                <div>
                  <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Κατηγορία' : 'Category'}</label>
                  <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      {categories.map((cat) => (
                        <SelectItem className={adminSelectItemClass} key={cat.id} value={cat.id}>{cat.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Λήξη (προαιρετικό)' : 'Expires (optional)'}</label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  style={adminDatePickerStyle}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/20 text-white">
                  {t('admin.cancel')}
                </Button>
                <Button onClick={handleSubmit} className="bg-primary hover:bg-primary/90">
                  {t('admin.save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-misel-card border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">User</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Τύπος' : 'Type'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Λήξη' : 'Expires'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Κατάσταση' : 'Status'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Ενέργειες' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {grants.map((grant) => {
              const user = users.find(u => u.id === grant.user_id);
              return (
                <tr key={grant.id} className="border-t border-white/5">
                  <td className="p-4 text-white">{user?.email || grant.user_id}</td>
                  <td className="p-4 text-gray-400">{grant.access_type}</td>
                  <td className="p-4 text-gray-400">{grant.expires_at || 'Never'}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-sm text-xs ${
                      grant.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {grant.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="p-4">
                    {grant.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        onClick={() => handleRevoke(grant.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Site Settings Management
const SiteSettings = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    hero_image_url: '',
    hero_title_el: '',
    hero_title_en: '',
    hero_subtitle_el: '',
    hero_subtitle_en: '',
    login_bg_image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/settings`, { headers });
      setSettings(res.data);
    } catch (e) {
      console.error('Failed to fetch settings', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/settings`, settings, { headers });
      toast.success(i18n.language === 'el' ? 'Οι ρυθμίσεις αποθηκεύτηκαν' : 'Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-misel-card animate-pulse rounded-sm" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-roboto font-bold text-2xl text-white">
          {i18n.language === 'el' ? 'Ρυθμίσεις Ιστοσελίδας' : 'Site Settings'}
        </h1>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-primary hover:bg-primary/90"
          data-testid="save-settings-btn"
        >
          {saving 
            ? (i18n.language === 'el' ? 'Αποθήκευση...' : 'Saving...') 
            : (i18n.language === 'el' ? 'Αποθήκευση' : 'Save Changes')
          }
        </Button>
      </div>

      <div className="space-y-8">
        {/* Hero Section Settings */}
        <div className="bg-misel-card border border-white/10 rounded-sm p-6">
          <h2 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
            <Image size={20} className="text-primary" />
            {i18n.language === 'el' ? 'Κεντρικό Banner (Hero)' : 'Hero Banner'}
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="text-sm text-gray-400 block mb-2">
                {i18n.language === 'el' ? 'URL Εικόνας Hero' : 'Hero Image URL'}
              </label>
              <Input
                value={settings.hero_image_url}
                onChange={(e) => setSettings({ ...settings, hero_image_url: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="https://..."
                data-testid="hero-image-url"
              />
              {settings.hero_image_url && (
                <div className="mt-3 aspect-video max-w-md rounded-sm overflow-hidden">
                  <img 
                    src={settings.hero_image_url} 
                    alt="Hero preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  {i18n.language === 'el' ? 'Τίτλος (Ελληνικά)' : 'Title (Greek)'}
                </label>
                <Input
                  value={settings.hero_title_el}
                  onChange={(e) => setSettings({ ...settings, hero_title_el: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Μάθε από τους καλύτερους"
                  data-testid="hero-title-el"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  {i18n.language === 'el' ? 'Τίτλος (Αγγλικά)' : 'Title (English)'}
                </label>
                <Input
                  value={settings.hero_title_en}
                  onChange={(e) => setSettings({ ...settings, hero_title_en: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Learn from the best"
                  data-testid="hero-title-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  {i18n.language === 'el' ? 'Υπότιτλος (Ελληνικά)' : 'Subtitle (Greek)'}
                </label>
                <Input
                  value={settings.hero_subtitle_el}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle_el: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Εξειδικευμένα σεμινάρια κομμωτικής..."
                  data-testid="hero-subtitle-el"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  {i18n.language === 'el' ? 'Υπότιτλος (Αγγλικά)' : 'Subtitle (English)'}
                </label>
                <Input
                  value={settings.hero_subtitle_en}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle_en: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Professional hairdressing seminars..."
                  data-testid="hero-subtitle-en"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Login/Register Background */}
        <div className="bg-misel-card border border-white/10 rounded-sm p-6">
          <h2 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
            <Image size={20} className="text-primary" />
            {i18n.language === 'el' ? 'Φόντο Σύνδεσης/Εγγραφής' : 'Login/Register Background'}
          </h2>
          
          <div>
            <label className="text-sm text-gray-400 block mb-2">
              {i18n.language === 'el' ? 'URL Εικόνας Φόντου' : 'Background Image URL'}
            </label>
            <Input
              value={settings.login_bg_image_url}
              onChange={(e) => setSettings({ ...settings, login_bg_image_url: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="https://..."
              data-testid="login-bg-url"
            />
            {settings.login_bg_image_url && (
              <div className="mt-3 aspect-video max-w-md rounded-sm overflow-hidden">
                <img 
                  src={settings.login_bg_image_url} 
                  alt="Login BG preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>
        </div>

        {/* Category Images Note */}
        <div className="bg-misel-card border border-white/10 rounded-sm p-6">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <FolderOpen size={20} className="text-primary" />
            {i18n.language === 'el' ? 'Εικόνες Κατηγοριών' : 'Category Images'}
          </h2>
          <p className="text-gray-400 text-sm">
            {i18n.language === 'el' 
              ? 'Οι κατηγορίες μπορούν να ενημερωθούν με slug, μεταφράσεις και image URL από τη διαχείριση κατηγοριών.'
              : 'Categories can be updated with slug, translations, and image URL from category management.'
            }
          </p>
          <Link to="/admin/categories" className="mt-4 inline-block">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
              {i18n.language === 'el' ? 'Διαχείριση Κατηγοριών' : 'Manage Categories'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ====================== Coupons Management ======================
const CouponsManagement = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', discount_type: 'percent', discount_value: 10,
    currency: 'EUR', max_redemptions: '', expires_at: '',
    applies_to_plan_ids: [], is_active: true,
  });

  const fetchCoupons = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/coupons`, { headers });
      setCoupons(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); /* eslint-disable-line */ }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ code: '', discount_type: 'percent', discount_value: 10, currency: 'EUR', max_redemptions: '', expires_at: '', applies_to_plan_ids: [], is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, discount_type: c.discount_type, discount_value: c.discount_value,
      currency: c.currency || 'EUR',
      max_redemptions: c.max_redemptions ?? '',
      expires_at: c.expires_at || '',
      applies_to_plan_ids: c.applies_to_plan_ids || [],
      is_active: c.is_active !== false,
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value) || 0,
        max_redemptions: form.max_redemptions === '' ? null : parseInt(form.max_redemptions),
        expires_at: form.expires_at || null,
      };
      if (editing) {
        await axios.put(`${API}/admin/coupons/${editing.id}`, payload, { headers });
        toast.success(i18n.language === 'el' ? 'Ενημερώθηκε' : 'Updated');
      } else {
        await axios.post(`${API}/admin/coupons`, payload, { headers });
        toast.success(i18n.language === 'el' ? 'Δημιουργήθηκε' : 'Created');
      }
      setDialogOpen(false);
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const del = async (id) => {
    if (!window.confirm(i18n.language === 'el' ? 'Αρχειοθέτηση κουπονιού;' : 'Archive coupon?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/coupons/${id}`, { headers });
      toast.success(i18n.language === 'el' ? 'Αρχειοθετήθηκε' : 'Archived');
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="text-white">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-roboto font-bold text-2xl text-white">
          {i18n.language === 'el' ? 'Κουπόνια Έκπτωσης' : 'Discount Coupons'}
        </h1>
        <Button data-testid="create-coupon-btn" onClick={openNew} className="bg-primary hover:bg-primary/90">
          <Plus size={18} className="mr-2" />
          {i18n.language === 'el' ? 'Νέο Κουπόνι' : 'New Coupon'}
        </Button>
      </div>

      <div className="bg-misel-card rounded-sm border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr>
              <th className="text-left px-4 py-3 text-sm text-gray-400">Code</th>
              <th className="text-left px-4 py-3 text-sm text-gray-400">{i18n.language === 'el' ? 'Έκπτωση' : 'Discount'}</th>
              <th className="text-left px-4 py-3 text-sm text-gray-400">{i18n.language === 'el' ? 'Χρήσεις' : 'Redemptions'}</th>
              <th className="text-left px-4 py-3 text-sm text-gray-400">{i18n.language === 'el' ? 'Λήξη' : 'Expires'}</th>
              <th className="text-left px-4 py-3 text-sm text-gray-400">Status</th>
              <th className="text-right px-4 py-3 text-sm text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white font-mono">{c.code}</td>
                <td className="px-4 py-3 text-white">
                  {c.discount_type === 'percent' ? `${c.discount_value}%` : `€${c.discount_value}`}
                </td>
                <td className="px-4 py-3 text-gray-400">
                  {c.redemptions || 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}
                </td>
                <td className="px-4 py-3 text-gray-400">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3">
                  {c.is_active ? <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-sm">Active</span> : <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-sm">Inactive</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" className="mr-2 border-white/20 text-white hover:bg-white/10" onClick={() => openEdit(c)}><Edit size={14} /></Button>
                  <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" title={i18n.language === 'el' ? 'Αρχειοθέτηση κουπονιού' : 'Archive coupon'} onClick={() => del(c.id)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-misel-card border-white/10 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? (i18n.language === 'el' ? 'Επεξεργασία Κουπονιού' : 'Edit Coupon') : (i18n.language === 'el' ? 'Νέο Κουπόνι' : 'New Coupon')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400">Code</label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="bg-misel-bg border-white/20 text-white font-mono uppercase" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Τύπος' : 'Type'}</label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="bg-misel-bg border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className={adminSelectContentClass}>
                    <SelectItem className={adminSelectItemClass} value="percent">Percent (%)</SelectItem>
                    <SelectItem className={adminSelectItemClass} value="amount">Fixed amount (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Τιμή' : 'Value'}</label>
                <Input type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="bg-misel-bg border-white/20 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Μέγιστες χρήσεις (κενό = ∞)' : 'Max redemptions (empty = ∞)'}</label>
                <Input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className="bg-misel-bg border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400">{i18n.language === 'el' ? 'Λήξη (κενό = χωρίς λήξη)' : 'Expires at (empty = none)'}</label>
                <Input type="datetime-local" value={form.expires_at ? form.expires_at.slice(0, 16) : ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="bg-misel-bg border-white/20 text-white" style={adminDatePickerStyle} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-white">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/20 text-white hover:bg-white/10">Cancel</Button>
              <Button data-testid="save-coupon-btn" onClick={submit} className="bg-primary hover:bg-primary/90">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ====================== Subscriptions & Stripe→Revolut Migration ======================
const SubscriptionsManagement = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const [allSubs, setAllSubs] = useState([]);
  const [stripeSubs, setStripeSubs] = useState({ configured: false, subscriptions: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [all, stripe] = await Promise.all([
        axios.get(`${API}/admin/subscriptions/all`, { headers }),
        axios.get(`${API}/admin/stripe/subscriptions`, { headers }),
      ]);
      setAllSubs(all.data || []);
      setStripeSubs(stripe.data || { configured: false, subscriptions: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, []);

  const cancelAllStripe = async () => {
    const msg = i18n.language === 'el'
      ? 'Θα ακυρωθούν ΟΛΕΣ οι ενεργές Stripe συνδρομές στο τέλος της τρέχουσας περιόδου. Οι πελάτες ΔΕΝ θα χρεωθούν ξανά. Διατηρούν πρόσβαση μέχρι τη λήξη της περιόδου που έχουν πληρώσει. Επιβεβαίωση;'
      : 'ALL active Stripe subscriptions will be cancelled at the end of the current period. Customers will NOT be charged again. They keep access until the paid period ends. Confirm?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/stripe/cancel-all-at-period-end`, {}, { headers });
      toast.success(`${res.data.cancelled_count} subscriptions cancelled at period end`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const cancelOneStripe = async (id) => {
    if (!window.confirm(i18n.language === 'el' ? `Ακύρωση της συνδρομής ${id} στο τέλος της περιόδου;` : `Cancel ${id} at period end?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/stripe/cancel-subscription/${id}`, {}, { headers });
      toast.success('Cancelled at period end');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const runRenewal = async () => {
    setBusy(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/subscriptions/run-renewal-now`, {}, { headers });
      toast.success(`${res.data.processed} renewals processed`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-white">Loading…</div>;

  const revolutSubs = allSubs.filter(s => s.provider === 'revolut');
  const otherSubs = allSubs.filter(s => s.provider !== 'revolut');

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-roboto font-bold text-2xl text-white mb-2">
          {i18n.language === 'el' ? 'Συνδρομές & Μετάβαση σε Revolut' : 'Subscriptions & Revolut Migration'}
        </h1>
        <p className="text-gray-400 text-sm">
          {i18n.language === 'el'
            ? 'Διαχείριση υπαρχουσών Stripe συνδρομών και αυτόματες ανανεώσεις Revolut'
            : 'Manage existing Stripe subscriptions and Revolut auto-renewals'}
        </p>
      </div>

      {/* Stripe Migration Section */}
      <div className="bg-misel-card rounded-sm border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-roboto font-bold text-lg text-white">Stripe → Revolut Migration</h2>
            <p className="text-gray-400 text-sm mt-1">
              {stripeSubs.configured
                ? `${stripeSubs.count || 0} ${i18n.language === 'el' ? 'ενεργές Stripe συνδρομές' : 'active Stripe subscriptions'}`
                : (i18n.language === 'el' ? 'Stripe δεν είναι συνδεδεμένο' : 'Stripe not configured')}
            </p>
          </div>
          {stripeSubs.configured && stripeSubs.count > 0 && (
            <Button
              data-testid="cancel-all-stripe-btn"
              onClick={cancelAllStripe}
              disabled={busy}
              className="bg-red-500 hover:bg-red-600"
            >
              {i18n.language === 'el' ? 'Ακύρωση ΟΛΩΝ στο τέλος περιόδου' : 'Cancel ALL at period end'}
            </Button>
          )}
        </div>
        {stripeSubs.subscriptions?.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="text-left px-3 py-2 text-gray-400">ID</th>
                <th className="text-left px-3 py-2 text-gray-400">Email</th>
                <th className="text-left px-3 py-2 text-gray-400">{i18n.language === 'el' ? 'Λήξη περιόδου' : 'Period ends'}</th>
                <th className="text-left px-3 py-2 text-gray-400">{i18n.language === 'el' ? 'Ακύρωση;' : 'Cancelled?'}</th>
                <th className="text-right px-3 py-2 text-gray-400">Action</th>
              </tr>
            </thead>
            <tbody>
              {stripeSubs.subscriptions.map((s) => (
                <tr key={s.id} className="border-b border-white/5">
                  <td className="px-3 py-2 text-gray-300 font-mono text-xs">{s.id}</td>
                  <td className="px-3 py-2 text-white">{s.customer_email || '—'}</td>
                  <td className="px-3 py-2 text-gray-400">{new Date(s.current_period_end_iso).toLocaleDateString()}</td>
                  <td className="px-3 py-2">
                    {s.cancel_at_period_end ? (
                      <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-sm">Pending</span>
                    ) : (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-sm">Active</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!s.cancel_at_period_end && (
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => cancelOneStripe(s.id)}>
                        {i18n.language === 'el' ? 'Ακύρωση' : 'Cancel'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Revolut Auto-Renewal Section */}
      <div className="bg-misel-card rounded-sm border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-roboto font-bold text-lg text-white">Revolut Auto-Renewal</h2>
            <p className="text-gray-400 text-sm mt-1">
              {i18n.language === 'el'
                ? 'Αυτόματη χρέωση κάθε 6 ώρες. Το scheduler τρέχει αυτόματα στον server.'
                : 'Auto-charge every 6 hours. Scheduler runs automatically on the server.'}
            </p>
          </div>
          <Button
            data-testid="run-renewal-now-btn"
            onClick={runRenewal}
            disabled={busy}
            className="bg-primary hover:bg-primary/90"
          >
            {i18n.language === 'el' ? 'Εκτέλεση τώρα' : 'Run now'}
          </Button>
        </div>
        <p className="text-white text-sm">
          <strong>{revolutSubs.length}</strong> {i18n.language === 'el' ? 'ενεργές συνδρομές Revolut' : 'active Revolut subscriptions'}
        </p>
      </div>

      {/* All Local Subscriptions */}
      <div className="bg-misel-card rounded-sm border border-white/10 p-6">
        <h2 className="font-roboto font-bold text-lg text-white mb-4">
          {i18n.language === 'el' ? 'Όλες οι τοπικές συνδρομές' : 'All local subscriptions'} ({allSubs.length})
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="text-left px-3 py-2 text-gray-400">User</th>
                <th className="text-left px-3 py-2 text-gray-400">Plan</th>
                <th className="text-left px-3 py-2 text-gray-400">Provider</th>
                <th className="text-left px-3 py-2 text-gray-400">Status</th>
                <th className="text-left px-3 py-2 text-gray-400">Period end</th>
                <th className="text-left px-3 py-2 text-gray-400">Migration</th>
              </tr>
            </thead>
            <tbody>
              {allSubs.slice(0, 50).map((s) => (
                <tr key={s.id} className="border-b border-white/5">
                  <td className="px-3 py-2 text-gray-300 font-mono text-xs">{(s.user_id || '').slice(0, 12)}</td>
                  <td className="px-3 py-2 text-white">{s.plan_id}</td>
                  <td className="px-3 py-2 text-gray-400">{s.provider || 'mock'}</td>
                  <td className="px-3 py-2">
                    {s.status === 'active' ? (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-sm">{s.status}</span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-sm">{s.status}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-400">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2">
                    {s.migration_pending_to_revolut && (
                      <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-sm">→ Revolut</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {allSubs.length > 50 && <p className="text-xs text-gray-500 pt-2">Showing first 50 of {allSubs.length}</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
